import { ExpandMore, FitnessCenter } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Container,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ThemeProvider,
    Typography,
    createTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import WendlerBBBPlan from './WendlerBBBPlan';

function OneRepMaxCalculator() {
    const [liftData, setLiftData] = useLocalStorage('liftData', {
        squat: { weight: '', reps: '' },
        benchPress: { weight: '', reps: '' },
        deadlift: { weight: '', reps: '' },
        overheadPress: { weight: '', reps: '' },
    });
    const [oneRepMaxes, setOneRepMaxes] = useLocalStorage('oneRepMaxes', null);
    const [expanded, setExpanded] = useState(!oneRepMaxes);
    const [open, setOpenDialog] = React.useState(false);


    useEffect(() => {
        setExpanded(!oneRepMaxes);
    }, [oneRepMaxes]);

    const handleClickOpen = () => {
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const increaseOneRepMaxes = () => {
        if (!oneRepMaxes) {
            setOpenDialog(true);
            return;
        }

        const increasedOneRepMaxes = Object.keys(oneRepMaxes).reduce((acc, lift) => {
            const increaseAmount = lift === 'squat' || lift === 'deadlift' ? 5 : 2.5;
            acc[lift] = parseFloat(oneRepMaxes[lift]) + increaseAmount;
            return acc;
        }, {});

        setOneRepMaxes(increasedOneRepMaxes);
    };

    const decreaseOneRepMaxes = () => {
        if (!oneRepMaxes) {
            setOpenDialog(true);
            return;
        }

        const decreasedOneRepMaxes = Object.keys(oneRepMaxes).reduce((acc, lift) => {
            const decreaseAmount = lift === 'squat' || lift === 'deadlift' ? 5 : 2.5;
            acc[lift] = parseFloat(oneRepMaxes[lift]) - decreaseAmount;
            return acc;
        }, {});

        setOneRepMaxes(decreasedOneRepMaxes);
    };


    const handleChange = (event, lift, field) => {
        const value = event.target.value;
        setLiftData({
            ...liftData,
            [lift]: { ...liftData[lift], [field]: value },
        });
    };

    const calculateOneRepMaxes = (e) => {
        e.preventDefault();

        const calculatedOneRepMaxes = Object.keys(liftData).reduce((acc, lift) => {
            const { weight, reps } = liftData[lift];
            acc[lift] = weight * (0.9 + reps / 30);
            return acc;
        }, {});

        setOneRepMaxes(calculatedOneRepMaxes);
    };

    const darkTheme = createTheme({
        palette: {
            mode: 'dark',
        },
    });

    const liftIcons = {
        squat: <FitnessCenter />,
        benchPress: <FitnessCenter />,
        deadlift: <FitnessCenter />,
        overheadPress: <FitnessCenter />,
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Container maxWidth="sm">
                <Typography variant="h6" style={{ marginTop: 15 }} component="h1" gutterBottom align="center">
                    5/3/1 - Maik Thiele
                </Typography>
                <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >

                        <Typography>Berechnungsformular</Typography>
                    </AccordionSummary>

                    <AccordionDetails>

                        <Box
                            component="form"
                            onSubmit={calculateOneRepMaxes}
                            noValidate
                            autoComplete="off"
                            sx={{ '& .MuiTextField-root': { m: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            {Object.keys(liftData).map((lift) => (
                                <Grid container spacing={2} key={lift} alignItems="center">
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label={`${lift.charAt(0).toUpperCase() + lift.slice(1)} (kg)`}
                                            variant="outlined"
                                            type="number"
                                            required
                                            value={liftData[lift].weight}
                                            onChange={(e) => handleChange(e, lift, 'weight')}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start" style={{ marginRight: '8px' }}>
                                                        {liftIcons[lift]}
                                                    </InputAdornment>
                                                ),

                                                inputProps: { min: "1" },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label={`${lift.charAt(0).toUpperCase() + lift.slice(1)} Reps`}
                                            variant="outlined"
                                            type="number"
                                            required
                                            value={liftData[lift].reps}
                                            onChange={(e) => handleChange(e, lift, 'reps')}
                                            InputProps={{
                                                inputProps: { min: "1", max: "10" },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                            <Button type="submit" variant="contained" sx={{ mt: 3, mb: 2 }}>
                                Berechnen
                            </Button>
                        </Box>
                    </AccordionDetails>
                </Accordion>
                {oneRepMaxes && (
                    <TableContainer component={Paper}>
                        <Table aria-label="1RM Tabelle">
                            <TableHead>
                                <TableRow>
                                    {Object.keys(oneRepMaxes).map((oneRepMax) => (
                                        <TableCell key={oneRepMax}>{oneRepMax.charAt(0).toUpperCase() + oneRepMax.slice(1)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {Object.keys(oneRepMaxes).map((oneRepMax) => (
                                        <TableCell key={oneRepMax}>{oneRepMaxes[oneRepMax].toFixed(2)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Buttons für die Steigerung und Rücknahme */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button onClick={decreaseOneRepMaxes} variant="contained">
                        Rücknahme
                    </Button>
                    <Button onClick={increaseOneRepMaxes} variant="contained">
                        Steigerung
                    </Button>
                </Box>
                {/* Modal-Dialog */}
                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>{"Hinweis"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Bitte berechnen Sie zuerst die 1RMs.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <WendlerBBBPlan oneRepMaxes={oneRepMaxes} />
            </Container>
        </ThemeProvider>
    );
}

export default OneRepMaxCalculator;
